import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProcessSection = () => {
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      id: 1,
      title: "Discovery & Strategy",
      subtitle: "Understanding Your Vision",
      description: "We begin every project with deep discovery sessions to understand your business goals, target audience, and unique challenges. This foundation ensures our solutions align perfectly with your objectives.",
      duration: "1-2 weeks",
      deliverables: ["Business Analysis Report", "User Persona Development", "Competitive Analysis", "Project Roadmap"],
      tools: ["Figma", "Miro", "Google Analytics", "Hotjar"],
      icon: "Search",
      color: "primary",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 2,
      title: "Design & Prototyping",
      subtitle: "Crafting the Experience",
      description: "Our design phase focuses on creating intuitive, beautiful interfaces that solve real user problems. We prototype extensively to validate concepts before development begins.",
      duration: "2-3 weeks",
      deliverables: ["Wireframes", "UI/UX Design", "Interactive Prototypes", "Design System"],
      tools: ["Figma", "Adobe Creative Suite", "Principle", "InVision"],
      icon: "Palette",
      color: "accent",
      image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 3,
      title: "Development & Integration",
      subtitle: "Building with Precision",
      description: "Using cutting-edge technologies and best practices, we build scalable, secure solutions that perform flawlessly across all devices and platforms.",
      duration: "4-8 weeks",
      deliverables: ["Frontend Development", "Backend Architecture", "API Integration", "Database Design"],
      tools: ["React/Next.js", "Node.js", "AWS", "MongoDB"],
      icon: "Code",
      color: "success",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 4,
      title: "Testing & Optimization",
      subtitle: "Ensuring Excellence",
      description: "Rigorous testing across multiple devices, browsers, and scenarios ensures your solution works perfectly for every user. We optimize for performance, accessibility, and SEO.",
      duration: "1-2 weeks",
      deliverables: ["Quality Assurance Testing", "Performance Optimization", "SEO Implementation", "Accessibility Audit"],
      tools: ["Jest", "Cypress", "Lighthouse", "GTmetrix"],
      icon: "Shield",
      color: "trust",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 5,
      title: "Launch & Support",
      subtitle: "Going Live Successfully",
      description: "We handle the complete launch process and provide ongoing support to ensure your solution continues to perform optimally and evolve with your business needs.",
      duration: "Ongoing",
      deliverables: ["Deployment", "Training Materials", "Documentation", "Ongoing Support"],
      tools: ["AWS/Vercel", "Google Analytics", "Monitoring Tools", "Support Portal"],
      icon: "Rocket",
      color: "conversion",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    }
  ];

  const frameworks = [
    {
      name: "Design Thinking",
      description: "Human-centered approach to innovation",
      icon: "Users"
    },
    {
      name: "Agile Methodology",
      description: "Iterative development with continuous feedback",
      icon: "RefreshCw"
    },
    {
      name: "Lean Startup",
      description: "Build-measure-learn cycles for rapid validation",
      icon: "TrendingUp"
    },
    {
      name: "DevOps Practices",
      description: "Continuous integration and deployment",
      icon: "GitBranch"
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
              <Icon name="Settings" size={24} className="text-white" />
            </div>
            <span className="text-accent font-semibold text-lg">Our Process</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            From Complexity to Simplicity
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our proven methodology transforms digital challenges into elegant solutions through structured phases and collaborative partnership.
          </p>
        </div>

        {/* Process Steps */}
        <div className="mb-20">
          {/* Step Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {processSteps?.map((step, index) => (
              <button
                key={step?.id}
                onClick={() => setActiveStep(index)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg brand-transition ${
                  activeStep === index
                    ? `bg-${step?.color} text-white`
                    : 'bg-white text-foreground hover:bg-surface'
                } brand-shadow`}
              >
                <Icon name={step?.icon} size={20} />
                <span className="font-medium">{step?.title}</span>
              </button>
            ))}
          </div>

          {/* Active Step Content */}
          <div className="bg-white rounded-2xl p-8 brand-shadow">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 bg-${processSteps?.[activeStep]?.color} rounded-lg flex items-center justify-center`}>
                    <Icon name={processSteps?.[activeStep]?.icon} size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">{processSteps?.[activeStep]?.title}</h3>
                    <p className="text-lg text-muted-foreground">{processSteps?.[activeStep]?.subtitle}</p>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                  {processSteps?.[activeStep]?.description}
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
                      <Icon name="Clock" size={16} className="text-accent" />
                      <span>Duration</span>
                    </h4>
                    <p className="text-muted-foreground">{processSteps?.[activeStep]?.duration}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
                      <Icon name="Tool" size={16} className="text-accent" />
                      <span>Key Tools</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {processSteps?.[activeStep]?.tools?.map((tool, index) => (
                        <span key={index} className="px-2 py-1 bg-surface text-foreground text-sm rounded">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="Package" size={16} className="text-accent" />
                    <span>Deliverables</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {processSteps?.[activeStep]?.deliverables?.map((deliverable, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        <span className="text-sm text-muted-foreground">{deliverable}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="relative">
                <Image
                  src={processSteps?.[activeStep]?.image}
                  alt={`${processSteps?.[activeStep]?.title} process illustration`}
                  className="w-full h-80 object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-sm font-semibold text-foreground">Step {activeStep + 1} of {processSteps?.length}</div>
                    <div className="text-xs text-muted-foreground mt-1">Click tabs above to explore each phase</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Frameworks */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">Problem-Solving Frameworks</h3>
            <p className="text-lg text-muted-foreground">
              We leverage proven methodologies to ensure consistent, high-quality outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {frameworks?.map((framework, index) => (
              <div key={index} className="bg-white rounded-xl p-6 brand-shadow text-center brand-transition brand-hover-lift">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={framework?.icon} size={24} className="text-primary" />
                </div>
                <h4 className="font-bold text-foreground mb-2">{framework?.name}</h4>
                <p className="text-sm text-muted-foreground">{framework?.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Process Benefits */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-4">Why Our Process Works</h3>
            <p className="text-white/90 max-w-2xl mx-auto">
              Our structured approach ensures predictable outcomes, transparent communication, and exceptional results
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Target" size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Predictable Results</h4>
              <p className="text-white/80">Structured phases eliminate surprises and ensure consistent quality delivery</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MessageCircle" size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Clear Communication</h4>
              <p className="text-white/80">Regular updates and milestone reviews keep everyone aligned throughout the project</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Zap" size={32} className="text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Faster Time-to-Market</h4>
              <p className="text-white/80">Efficient workflows and proven methodologies accelerate project delivery</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;